import { setViewer } from '@/auth/store/viewerSlice';
import { ERROR_CODES } from '@/core/api';
import { loggedOutViewer } from '@/core/viewer';
import store from '@/store';

import { authSession } from './authSession';
import { isMfaSetupRequired } from './cognitoLogin';
import { discardCognitoSession, endCognitoSession, isCognitoSessionActive } from './cognitoSession';

// The copy the other signup modals show for the same case (ENG-6103).
export const MFA_SETUP_AT_LOGIN_MESSAGE =
  'Your account requires two-factor authentication setup. Please use the main login page to complete setup.';

/** A signup funnel step the api refused until the user sets up MFA at login. */
export class MfaSetupAtLoginError extends Error {
  constructor() {
    super(MFA_SETUP_AT_LOGIN_MESSAGE);
    this.name = 'MfaSetupAtLoginError';
  }
}

/**
 * ENG-6106: the api refuses a Cognito session with `auth.mfa_setup_required`
 * when the user had MFA before the migration and holds no Cognito factor. A
 * signup modal cannot enrol one, so sign out of Cognito in this tab (other
 * tabs are refused the same way on their next request), forget the viewer (a
 * reopened modal must not resume from it and fire anonymous calls), and answer
 * with the copy that sends the user to the login page. Anything else, or no
 * Cognito session, passes through unchanged.
 */
export async function mfaSetupAtLogin(err: unknown): Promise<unknown> {
  if (!isCognitoSessionActive() || !isMfaSetupRequired(err)) return err;
  await discardCognitoSession();
  authSession.clearToken();
  store.dispatch(setViewer(loggedOutViewer));
  return new MfaSetupAtLoginError();
}

/**
 * signup() reports the same refusal as `auth.mfa_enrollment_required`, which
 * the account step already turns into its "Sign in to continue" screen. Only
 * the Cognito session it started has to go.
 */
export function endSessionRefusedAtSignup(err: unknown): void {
  const code = typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : null;
  if (isCognitoSessionActive() && code === ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED) {
    void endCognitoSession();
  }
}
